import loadingImgSrc from '@/assets/images/load.gif'
import { Tokens } from '@/assets/tokens'
import { Checkbox, Icon } from '@/components/core'
import { Down, Search, Up } from '@icon-park/react'
import React, { ReactNode, RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MultiSelectionBinder, SelectionOption, SelectionType } from '../Selection.hooks'
import { CleanButton } from '../Selection.subcomponents'
import {
  ComponentContainer,
  Label,
  SearchContainer,
  SearchInput,
  SelectedItem,
  SelectedItemsText,
  SelectionContainer,
  Selector,
  SelectorOptions,
} from './MultiSelector.styles'

type Props = {
  allowClear?: boolean
  allowExclusion?: boolean
  binder: MultiSelectionBinder
  children?: ReactNode
  disabled?: boolean
  label?: string
  loading?: boolean
  options: SelectionOption[]
  placeholder?: string
  required?: boolean
  title?: string
  $fit?: boolean
  $inverted?: boolean
  onSearch?: (searchTerm: string) => void
  // Quando true, a lista de opções é renderizada num portal (position: fixed,
  // fora da árvore do componente) em vez de position: absolute dentro dele —
  // evita que um ancestral com overflow:auto/hidden (ex: um modal com scroll
  // próprio) corte a lista. Desligado por padrão pra não mudar o
  // comportamento de nenhum uso existente; mesma técnica já usada em
  // PolarityDropdown/HistoryPopover.
  renderOptionsInPortal?: boolean
}

export function MultiSelector({
  allowClear = true,
  allowExclusion = false,
  binder,
  children,
  disabled = false,
  label,
  loading = false,
  options,
  placeholder,
  required = false,
  title,
  $fit = false,
  $inverted = false,
  onSearch,
  renderOptionsInPortal = false,
}: Props): JSX.Element {
  const ref: RefObject<HTMLDivElement> = React.createRef()
  const searchInputRef = useRef<HTMLInputElement>(null)
  const optionsRef = useRef<HTMLUListElement>(null)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({})

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const clickedInsideTrigger = ref.current?.contains(target)
      const clickedInsidePortaledOptions = optionsRef.current?.contains(target)
      if (!clickedInsideTrigger && !clickedInsidePortaledOptions) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [ref])

  useEffect(() => {
    if (!renderOptionsInPortal || !isOpen) return

    const updatePosition = () => {
      if (!ref.current) return
      const rect = ref.current.getBoundingClientRect()
      setPortalStyle({
        position: 'fixed',
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      })
    }

    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [renderOptionsInPortal, isOpen, ref])

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100)
    } else {
      setSearchTerm('')
    }
  }, [isOpen])

  useEffect(() => {}, [binder.selectedOptions])

  const handleToggleSelection = useCallback(
    (option: SelectionOption, type: SelectionType = 'include') => {
      binder.onToggle(option, type)
    },
    [binder.onToggle],
  )

  const handleOptionClick = useCallback(
    (option: SelectionOption) => {
      if (!allowExclusion) {
        handleToggleSelection(option, 'include')
        return
      }
      const isSelected = binder.selectedOptions.some((item) => item.value === option.value)
      const isExcluded = binder.excludedOptions.some((item) => item.value === option.value)
      if (isExcluded) {
        handleToggleSelection(option, 'exclude') // excluded -> neutro
      } else if (isSelected) {
        handleToggleSelection(option, 'exclude') // incluído -> excluído
      } else {
        handleToggleSelection(option, 'include') // neutro -> incluído
      }
    },
    [allowExclusion, binder.selectedOptions, binder.excludedOptions, handleToggleSelection],
  )

  const handleClearAll = () => {
    binder.clearAll()
    setTimeout(() => setIsOpen(false), 100)
  }

  const hasValue = binder.selectedOptions.length > 0 || binder.excludedOptions.length > 0

  let displayText = ''
  if (hasValue) {
    const includedText = binder.selectedOptions.map((opt) => opt.text).join(', ')
    const excludedText = binder.excludedOptions.map((opt) => `!${opt.text}`).join(', ')
    const parts = [includedText, excludedText].filter(Boolean)
    displayText = parts.join(', ')
  } else {
    displayText = placeholder || ''
  }

  const placeholderColor = $inverted ? Tokens.ColorWhite : Tokens.ColorTextTertiary

  const filteredOptions = options.filter((option) => {
    const matchesSearch = option.text.toLowerCase().includes(searchTerm.toLowerCase())
    const isSelected = binder.selectedOptions.some((item) => item.value === option.value)
    const isExcluded = binder.excludedOptions.some((item) => item.value === option.value)
    return isSelected || isExcluded || matchesSearch
  })

  const isAllFilteredSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((option) => binder.selectedOptions.some((item) => item.value === option.value))

  const isAllFilteredExcluded =
    allowExclusion &&
    filteredOptions.length > 0 &&
    filteredOptions.every((option) => binder.excludedOptions.some((item) => item.value === option.value))

  const selectAllLabel =
    isAllFilteredExcluded ? 'Excluir todos'
    : isAllFilteredSelected ? 'Incluir todos'
    : 'Selecionar todos'

  const handleToggleSelectAll = () => {
    let target: SelectionType | 'neutral'
    if (!allowExclusion) {
      target = isAllFilteredSelected ? 'neutral' : 'include'
    } else if (isAllFilteredSelected) {
      target = 'exclude'
    } else if (isAllFilteredExcluded) {
      target = 'neutral'
    } else {
      target = 'include'
    }

    filteredOptions.forEach((option) => {
      const isSelected = binder.selectedOptions.some((item) => item.value === option.value)
      const isExcluded = binder.excludedOptions.some((item) => item.value === option.value)
      if (target === 'include' && !isSelected) {
        handleToggleSelection(option, 'include')
      } else if (target === 'exclude' && !isExcluded) {
        handleToggleSelection(option, 'exclude')
      } else if (target === 'neutral') {
        if (isSelected) handleToggleSelection(option, 'include')
        if (isExcluded) handleToggleSelection(option, 'exclude')
      }
    })
  }

  const optionsList = (options.length > 0 || isOpen) && (
    <Selector ref={optionsRef} $show={isOpen} style={renderOptionsInPortal ? portalStyle : undefined}>
      <SearchContainer onClick={(e) => e.stopPropagation()}>
        <Icon value={Search} size={16} color={Tokens.ColorUIPrimary} />
        <SearchInput
          ref={searchInputRef}
          type="text"
          placeholder="Pesquisar..."
          value={searchTerm}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            setSearchTerm(e.target.value)
            onSearch?.(e.target.value)
          }}
        />
      </SearchContainer>

      {filteredOptions.length > 0 && (
        <SelectorOptions onClick={(e) => e.stopPropagation()} $selected={isAllFilteredSelected || isAllFilteredExcluded}>
          <Checkbox
            checked={isAllFilteredSelected || isAllFilteredExcluded}
            onChange={handleToggleSelectAll}
            color={isAllFilteredExcluded ? 'red' : 'green'}
            width={16}
            aria-label={selectAllLabel}
          />
          <span
            onClick={(e) => {
              e.stopPropagation()
              handleToggleSelectAll()
            }}
            style={{ fontWeight: 600 }}>
            {selectAllLabel}
          </span>
        </SelectorOptions>
      )}

      {filteredOptions.map((option) => {
        const isSelected = binder.selectedOptions.some((item) => item.value === option.value)
        const isExcluded = binder.excludedOptions.some((item) => item.value === option.value)
        return (
          <SelectorOptions key={option.value} onClick={(e) => e.stopPropagation()} $selected={isSelected || isExcluded}>
            <Checkbox
              checked={isSelected || isExcluded}
              onChange={() => handleOptionClick(option)}
              color={isExcluded ? 'red' : 'green'}
              width={16}
              aria-label={
                isExcluded ? 'Excluído'
                : isSelected ?
                  'Incluído'
                : 'Selecionar'
              }
            />
            <span
              onClick={(e) => {
                e.stopPropagation()
                handleOptionClick(option)
              }}>
              {option.text}
            </span>
          </SelectorOptions>
        )
      })}

      {filteredOptions.length === 0 && (
        <SelectorOptions $selected={false} style={{ justifyContent: 'center' }}>
          Nenhum resultado encontrado
        </SelectorOptions>
      )}
    </Selector>
  )

  return (
    <ComponentContainer ref={ref} $fit={$fit}>
      {label && (
        <Label>
          {label}
          {required && '*'}
        </Label>
      )}

      <SelectionContainer
        onClick={() => !disabled && setIsOpen((v) => !v)}
        $inverted={$inverted}
        $disabled={disabled}
        title={title}>
        {children}
        <SelectedItem $placeholderColor={placeholderColor} $hasValue={hasValue}>
          <SelectedItemsText>{displayText}</SelectedItemsText>
        </SelectedItem>

        {hasValue && allowClear && <CleanButton onClick={handleClearAll} />}

        {loading ?
          <img alt="loading" src={loadingImgSrc} />
        : <Icon value={isOpen ? Up : Down} size={16} color={$inverted ? Tokens.ColorWhite : Tokens.ColorUIPrimary} />}

        {!renderOptionsInPortal && optionsList}
      </SelectionContainer>

      {renderOptionsInPortal && isOpen && optionsList && createPortal(optionsList, document.body)}
    </ComponentContainer>
  )
}
